import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { DropdownButtonType, List, ListItem, SingleSelectDropdown } from '@tableau/widgets';
import { ContentType } from '../models/ContentType';
import { ContentDefinitions } from '../models/contentDefinitions';
import { SortSelection } from '../models/SortSelection';
import { NavigationService } from '../services/NavigationService';
import { AppState } from '../../app/state/AppState';
import { orderBy } from '../../search/actions/OrderByAction';
import { TelemetryService } from '../services/TelemetryService';
import { getSortSelectionFromUrl } from '../utils/SortUtils';

export interface SortByDropdownOwnProps {
  readonly contentType: ContentType;
}

export type SortByDropdownProps = SortByDropdownOwnProps & SortByDropdownStateProps & SortByDropdownDispatchProps & RouteComponentProps;

export function SortByDropdown(props: SortByDropdownProps): JSX.Element {
  const sortByList = ContentDefinitions[props.contentType].getSortOptions();

  const [sortName, setSortName] = React.useState(props.sortBy?.name);

  function onDropDownValueChanged(value: string) {
    const selection = sortByList.find((x) => x.primarySort.value === value);
    setSortName(selection?.name);
    if (selection) {
      TelemetryService.instance.sortEvent(selection.name, props.contentType, true);
      props.orderBy(selection);
    }
    NavigationService.instance.navigateBySortOption(selection);
  }

  function getPrimarySorBytList(list: SortSelection[]): ListItem[] {
    return list.map(({ primarySort }) => primarySort);
  }

  function getSortValue(sortNameFromState?: string) {
    // We need to persist the value between content types. If an option from the global state is not available for the newly selected
    // content type this will instead use the default sort value available for the current content type. Since some content types like view and table
    // have the same sort option of Popularity but use a different field in Crass we should always check to see if its similar based on the name.
    if (sortNameFromState) {
      const sortIsAvailableForContentType = sortByList.find((x) => x.name === sortNameFromState);
      return sortIsAvailableForContentType ? sortIsAvailableForContentType.primarySort.value : sortByList[0].primarySort.value;
    } else {
      const value = getSortSelectionFromUrl(undefined, sortByList);
      return value ? value?.primarySort.value : sortByList[0].primarySort.value;
    }
  }

  return (
    <SingleSelectDropdown
      id="search-sort-dropdown"
      label=""
      list={<List items={getPrimarySorBytList(sortByList)} />}
      value={getSortValue(sortName)}
      type={DropdownButtonType.Line}
      onChange={(value) => onDropDownValueChanged(value)}
    />
  );
}

export interface SortByDropdownStateProps {
  sortBy?: SortSelection;
}

export interface SortByDropdownDispatchProps {
  orderBy: (sortSelection: SortSelection) => void;
}

const mapStateToProps = (state: AppState) => ({
  sortBy: state.search.sortBy,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      orderBy,
    },
    dispatch
  );

const SortByDropdownWithRouter = withRouter(SortByDropdown);

export const ConnectedSortByDropdown: React.ComponentClass<SortByDropdownOwnProps> = connect(
  mapStateToProps,
  mapDispatchToProps
)(SortByDropdownWithRouter);
